import { colors, spacing } from '@akin-home/theme';
import { showErrorToast } from '@akin-home/toast/native';
import React, { useEffect, useState } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { Switch, SwitchProps } from 'react-native-switch';
import { useRobotSetting } from '../../info';
import { RobotSettings } from '../../info/types';
import { RobotDevice } from '../../types';
import {
  RobotSettingsHeader,
  RobotSettingsHeaderProps,
} from './RobotSettingsHeader';

export type RobotSettingsSwitchProps = {
  robot: RobotDevice | undefined;
  settingName: keyof RobotSettings;
  style?: StyleProp<ViewStyle>;
  headerProps?: RobotSettingsHeaderProps;
  switchProps?: SwitchProps;
  disabled?: boolean;
  onValueChange?: (newValue: boolean) => void;
};

export const RobotSettingsSwitch = (props: RobotSettingsSwitchProps) => {
  const { robot, settingName } = props;

  // Local switch state that tracks but is decoupled from server state for UI responsiveness
  const [value, setValue] = useState<boolean>(false);

  const {
    query: { data: settingState, isLoading, isError },
    mutation: { mutate: updateSetting },
  } = useRobotSetting(robot, settingName, {
    mutation: {
      onError: (error) => {
        if (error) {
          showErrorToast({
            text1: 'Update Setting Error',
            text2: `Failed to update robot settings. Please try again.`,
          });
        }
      },
    },
  });

  // If the setting State from the robot changes, we update the local state as well
  useEffect(() => {
    if (settingState !== undefined) {
      setValue(settingState);
    }
  }, [settingState]);

  const handleToggleSwitch = async (newValue: boolean) => {
    setValue(newValue);
    props.onValueChange?.(newValue);
    updateSetting(newValue);
  };

  const switchDisabled =
    (props.disabled ?? false) ||
    isLoading ||
    isError ||
    settingState === undefined ||
    settingState === null;

  return (
    <View style={[styles.container, props.style]}>
      <RobotSettingsHeader
        disabled={switchDisabled}
        {...(props?.headerProps ?? {})}
      />
      <Switch
        disabled={switchDisabled}
        value={!switchDisabled && value}
        onValueChange={handleToggleSwitch}
        switchLeftPx={3}
        switchRightPx={3}
        barHeight={32}
        circleSize={25}
        renderActiveText={false}
        renderInActiveText={false}
        backgroundActive={switchDisabled ? colors.grey.dim : colors.primary[70]}
        backgroundInactive={switchDisabled ? colors.grey.dim : 'grey'}
        circleBorderWidth={0}
        changeValueImmediately
        {...props.switchProps}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing[5],
  },
  text: {
    fontSize: 16,
  },
});
